import { Component, Injector, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, Card, CardService, Task, TasksService, TaskStatusEnum, TaskTodayGroup, TaskTodayReading } from 'lib-trend-core';
import { catchError, forkJoin, interval, merge, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { ActivityFilters } from '../task.component';

/** The five groups, in the order the day is executed (RF-36). */
const GROUPS: Array<{ key: TaskTodayGroup; label: string; icon: string }> = [
  { key: 'overdue', label: 'task.today.groupOverdue', icon: 'mdi-alert-circle-outline' },
  { key: 'dueNow', label: 'task.today.groupDueNow', icon: 'mdi-clock-alert-outline' },
  { key: 'laterToday', label: 'task.today.groupLater', icon: 'mdi-clock-outline' },
  { key: 'untimed', label: 'task.today.groupUntimed', icon: 'mdi-calendar-blank-outline' },
  { key: 'concluded', label: 'task.today.groupConcluded', icon: 'mdi-check-circle-outline' },
];

/** Freshness (RNF-02, plan AD-12): the reading reloads on this cadence and right after every own action. */
const REFRESH_MS = 60_000;

/**
 * The Today view (SPEC-2026-05 … RF-36 to RF-41, RF-44): the user's day in five groups, the
 * day's progress, the time left until the next timed commitment, and — on selection — the deal's
 * context in a side panel built from the existing deal radar and the deal's activities.
 * Everything about "today" comes from the API, in the company's zone (RF-06).
 */
@Component({
  selector: 'app-task-today',
  templateUrl: './task-today.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TaskTodayComponent extends AbstractComponent implements OnInit, OnDestroy {

  private currentFilters: ActivityFilters = {};
  private readonly reload$ = new Subject<void>();

  @Input() set filters(value: ActivityFilters) {
    this.currentFilters = value ?? {};
    this.reload$.next();
  }

  readonly groups = GROUPS;
  reading?: TaskTodayReading;
  override loading = true;
  failed = false;
  now = new Date();

  selectedTask?: Task;
  panelCard?: Card;
  panelActivities: Array<Task> = [];
  panelLoading = false;
  panelHidden = false;

  constructor(
    injector: Injector,
    private readonly tasksService: TasksService,
    private readonly cardService: CardService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    // One stream: the first load, the 60 s cadence and every explicit reload.
    merge(timer(0, REFRESH_MS), this.reload$)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.tasksService.today(this.currentFilters).pipe(catchError(() => of(null)))),
      )
      .subscribe((reading) => {
        this.loading = false;
        this.failed = !reading;
        if (reading) {
          this.reading = reading;
          this.now = new Date();
        }
      });

    // The countdown is recomputed in the browser between reads (RF-38).
    interval(30_000).pipe(takeUntil(this.destroy$)).subscribe(() => (this.now = new Date()));
  }

  refresh(): void {
    this.loading = true;
    this.reload$.next();
  }

  activitiesOf(group: TaskTodayGroup): Array<Task> {
    return this.reading?.groups?.[group] ?? [];
  }

  get isEmpty(): boolean {
    return !!this.reading && this.groups.every((group) => this.activitiesOf(group.key).length === 0);
  }

  /** "Próxima em N min · responsável" (RF-38), from the reading's `next`, counted down locally. */
  get nextCountdown(): { key: string; params: Record<string, unknown> } | null {
    const next = this.reading?.next;
    if (!next?.dueAt) return null;

    const minutesLeft = Math.ceil((new Date(next.dueAt).getTime() - this.now.getTime()) / 60_000);
    if (minutesLeft <= 0) return { key: 'task.today.nextNow', params: {} };
    if (minutesLeft >= 60) {
      return { key: 'task.today.nextInHours', params: { hours: Math.floor(minutesLeft / 60), minutes: minutesLeft % 60 } };
    }
    return { key: 'task.today.nextIn', params: { minutes: minutesLeft } };
  }

  get nextResponsible(): string {
    return this.reading?.next?.user?.name ?? '';
  }

  /** An action changed an activity: reload at once (RNF-02) and refresh the panel if it is the selected deal. */
  onChanged(): void {
    this.reload$.next();
    if (this.selectedTask) this.loadPanel(this.selectedTask);
  }

  /** RF-40: the deal's context, without leaving the view. */
  onSelected(task: Task): void {
    this.selectedTask = task;
    this.loadPanel(task);
  }

  closePanel(): void {
    this.selectedTask = undefined;
    this.panelCard = undefined;
    this.panelActivities = [];
    this.panelHidden = false;
  }

  private loadPanel(task: Task): void {
    const idCard = `${(task?.card as { _id?: unknown })?._id ?? ''}`;
    this.panelCard = undefined;
    this.panelActivities = [];
    this.panelHidden = false;

    // A deal outside the user's visibility: say so, show nothing of it (RF-44, E4 validation N2).
    if (task?.dealHidden) {
      this.panelHidden = true;
      return;
    }

    // No deal joined: the activity has none.
    if (!idCard) return;

    this.panelLoading = true;
    forkJoin({
      card: this.cardService.getById(idCard),
      activities: this.tasksService.listByCard(idCard).pipe(catchError(() => of([] as Array<Task>))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ card, activities }) => {
          this.panelLoading = false;
          this.panelCard = card;
          this.panelActivities = activities ?? [];
        },
        error: () => {
          // 404 is what the API answers for a deal the user may not see (RF-44, RNF-04).
          this.panelLoading = false;
          this.panelHidden = true;
        },
      });
  }

  /** The deal's upcoming activities, nearest first (RF-40). */
  get upcoming(): Array<Task> {
    return this.panelActivities
      .filter((activity) => activity.status !== TaskStatusEnum.DONE && !!activity.deadlineAt)
      .sort((a, b) => new Date(a.deadlineAt).getTime() - new Date(b.deadlineAt).getTime());
  }

  get panelStageName(): string {
    return (this.panelCard?.stage as { name?: string })?.name ?? '';
  }

  override ngOnDestroy(): void {
    this.reload$.complete();
    super.ngOnDestroy();
  }
}
